import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import ClassEditPage from './ClassEditPage.tsx';
import { StudentClassPage, type StudentClassData } from './StudentClassPage.tsx';
import './css/ClassEditPage.css';

function ClassPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [role, setRole] = useState<'teacher' | 'student' | null>(null);
  const [study, setStudy] = useState<StudentClassData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRole(null);
    setStudy(null);
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/classes/${id}/study`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 401) {
          navigate('/login');
          return;
        }
        if (!res.ok) {
          setError(data.message || t('classStudy.loadError'));
          return;
        }
        if (data.role === 'teacher') {
          setRole('teacher');
          return;
        }
        setRole('student');
        setStudy(data as StudentClassData);
      } catch {
        if (!cancelled) setError(t('classStudy.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isAuthenticated, navigate, t]);

  if (role === 'teacher') {
    return <ClassEditPage />;
  }

  if (authLoading || loading) {
    return (
      <div className="class-edit-page">
        <div className="class-edit-loading">{t('classStudy.loading')}</div>
      </div>
    );
  }

  if (error || !study) {
    return (
      <div className="class-edit-page">
        <div className="class-edit-card class-edit-create">
          <h1>{t('education.title')}</h1>
          <p className="class-edit-create-desc">{error || t('classStudy.notFound')}</p>
          <Link to="/my-class" className="class-edit-btn class-edit-btn-primary" style={{ marginTop: 12, display: 'inline-flex' }}>
            {t('education.title')}
          </Link>
        </div>
      </div>
    );
  }

  return <StudentClassPage key={study.id} data={study} />;
}

export default ClassPage;
